import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { supabase, supabaseAdminClient } from '../../Supabase/supabaseRealtimeClient';
import { Medico } from '../../types/telemedicina';
import { sendInvite } from '../../Supabase/supabaseClient';

interface AddMedicoDialogProps {
  open: boolean;
  onClose: () => void;
  medicoToEdit?: Medico | null;
}

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;



const AddMedicoDialog: React.FC<AddMedicoDialogProps> = ({ open, onClose, medicoToEdit }) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [crm, setCrm] = useState('');
  const [ufCrm, setUfCrm] = useState('');
  const [especialidade, setEspecialidade] = useState('');
  const [status, setStatus] = useState<'ativo' | 'inativo'>('ativo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (medicoToEdit) {
      setNome(medicoToEdit.nome);
      setCrm(medicoToEdit.crm);
      setUfCrm(medicoToEdit.uf_crm || '');
      setEspecialidade(medicoToEdit.especialidade);
      setStatus(medicoToEdit.status);
      setEmail('');
      setPassword('');
    } else {
      setNome('');
      setEmail('');
      setPassword('');
      setCrm('');
      setUfCrm('');
      setEspecialidade('');
      setStatus('ativo');
    }
    setError(null);
  }, [medicoToEdit, open]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!nome || !crm || !especialidade) {
        throw new Error('Preencha os campos obrigatórios.');
      }

      if (medicoToEdit) {
        // Atualizar
        const { error: updateError } = await supabase
          .from('medicos')
          .update({
            nome,
            crm,
            uf_crm: ufCrm,
            especialidade,
            status
          })
          .eq('id', medicoToEdit.id);

        if (updateError) throw updateError;
      } else {
        // Criar Novo
        if (!email) throw new Error('Email é obrigatório para novos médicos.');
        if (!password) throw new Error('A senha é obrigatória.');

        const trimmedEmail = email.trim();
        const registrationPassword = password;

        // Criar novo usuário no Auth e no Banco via Edge Function (Atômico)
        const tableData = {
          nome,
          crm,
          uf_crm: ufCrm,
          especialidade,
          status: 'ativo'
        };
        
        const { user } = await sendInvite(trimmedEmail, registrationPassword, 'medico', tableData);
        if (!user) throw new Error('Falha ao criar médico via Function.');
      }

      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar médico:', err);
      setError(err.message || 'Erro desconhecido ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{medicoToEdit ? 'Editar Médico' : 'Adicionar Médico'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          margin="dense"
          label="Nome Completo"
          fullWidth
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          required
        />
        
        {!medicoToEdit && (
          <>
            <TextField
              margin="dense"
              label="Email (para login)"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              helperText="Um convite será enviado para este email."
            />
            <TextField
              margin="normal"
              label="Senha"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            margin="dense"
            label="CRM"
            fullWidth
            value={crm}
            onChange={(e) => setCrm(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="UF"
            value={ufCrm}
            onChange={(e) => setUfCrm(e.target.value)}
            sx={{ width: 100 }}
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
          />
        </Box>

        <TextField
          margin="dense"
          label="Especialidade"
          fullWidth
          value={especialidade}
          onChange={(e) => setEspecialidade(e.target.value)}
          required
        />

        {medicoToEdit && (
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as 'ativo' | 'inativo')}
            >
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
            </Select>
          </FormControl>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMedicoDialog;
